import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import DataTable from "../ui/DataTable";
import ConfirmDialog from "../ui/ConfirmDialog";
import { customizationService } from "../../services/customizationService";
import { projectService } from "../../services/projectService";
import toast from "react-hot-toast";

const MAX_VARIABLES = 50;

const VariablesTab = ({ clientId }) => {
  const [projects, setProjects] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("global");
  const [projectId, setProjectId] = useState("");
  const [value, setValue] = useState("");
  const [isSecure, setIsSecure] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedVar, setSelectedVar] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadVariables = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await customizationService.variablesList({
        client_id: clientId,
      });
      setVariables(res?.data?.variables || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load variables");
      setVariables([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadProjects = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await projectService.getProjects({
        client_id: clientId,
        page: 1,
        limit: 500,
        is_active: true,
        is_deleted: false,
      });
      const list = res?.data?.projects || res?.projects || [];
      setProjects(list);
      if (list.length && !projectId) setProjectId(list[0].project_id);
    } catch (e) {
      console.error(e);
    }
  }, [clientId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadVariables();
  }, [loadVariables]);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setScope("global");
    setProjectId(projects[0]?.project_id || "");
    setValue("");
    setIsSecure(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setName(row.name || "");
    setScope(row.scope || "global");
    setProjectId(row.project_id || "");
    setValue(row.value_display === "****" ? "" : row.value_display || "");
    setIsSecure(!!row.is_secure);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Name is required");
      return;
    }
    if (variables.length >= MAX_VARIABLES && !editId) {
      toast.error(`Maximum of ${MAX_VARIABLES} variables per client.`);
      return;
    }
    if (scope === "project" && !projectId) {
      toast.error("Please select a project for project-level variable.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await customizationService.variablesUpdate(editId, {
          value: value,
          is_secure: isSecure,
        });
        toast.success("Variable updated");
      } else {
        await customizationService.variablesCreate({
          name: n,
          scope,
          project_id: scope === "project" ? projectId : null,
          value,
          is_secure: isSecure,
        });
        toast.success("Variable created");
      }
      setModalOpen(false);
      loadVariables();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setSelectedVar(row);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedVar) return;
    setDeleteLoading(true);
    try {
      await customizationService.variablesDelete(selectedVar.id);
      toast.success("Variable deleted");
      setDeleteDialog(false);
      setSelectedVar(null);
      loadVariables();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (_, row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: "value",
      header: "Value",
      render: (_, row) => (
        <span className="font-mono text-sm">
          {row.value_display === "****" ? "••••••••" : row.value_display || "—"}
        </span>
      ),
    },
    {
      key: "scope",
      header: "Type",
      render: (_, row) => (
        <span>{row.scope === "global" ? "Global" : "Project"}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-600"
            onClick={() => openDelete(row)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-gray-600 text-sm mt-1">
            Global and project-level variables for automation. Reference with
            &#123;&#123;variable_name&#125;&#125;.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.name || p.project_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        <Button
          onClick={openCreate}
          disabled={variables.length >= MAX_VARIABLES}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Variables
        </Button>
        </div>

      </div>
      <p className="text-xs text-gray-500">
        Please note: Maximum of {MAX_VARIABLES} Variables are allowed to add.
      </p>

      <div className="card overflow-hidden">
        <DataTable
          data={variables}
          columns={columns}
          loading={loading}
          emptyMessage="No variables. Add one above."
        />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editId ? "Edit Variable" : "Add New Variable"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. staging_url"
                disabled={!!editId}
              />
            </div>
            {!editId && (
              <>
                <div>
                  <Label>Type</Label>
                  <Select value={scope} onValueChange={setScope}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {scope === "project" && (
                  <div>
                    <Label>Project</Label>
                    <Select value={projectId} onValueChange={setProjectId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.project_id} value={p.project_id}>
                            {p.name || p.project_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            <div>
              <Label>Value</Label>
              <Input
                type={isSecure ? "password" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={isSecure ? "••••••••" : "Enter value"}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="var-secure"
                checked={isSecure}
                onChange={(e) => setIsSecure(e.target.checked)}
              />
              <Label htmlFor="var-secure">
                Secure (value encrypted and masked in UI)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setSelectedVar(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Variable"
        message={`Are you sure you want to delete "${selectedVar?.name}"? It may be used in automation.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default VariablesTab;
